import React from 'react';
import PropTypes from 'prop-types';
import enhanceWithClickOutside from 'react-click-outside';
import {
    OptionsContainer,
    OptionButton,
    StyledTooltip,
    OptionItem,
    OptionsList,
    OptionText,
    OptionIcon,
} from './DrawingToolOptions.styled';

class DrawingToolOptions extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeOption: props.options.find(option => option === props.value),
            showOptions: false,
            zIndex: 1,
        };

        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.setActiveOption = this.setActiveOption.bind(this);
        this.toggleOptions = this.toggleOptions.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { value } = this.props;
        if (value !== nextProps.value) {
            this.setState({
                activeOption: nextProps.value,
            });
        }
    }

    handleClickOutside() {
        this.setState({ showOptions: false, zIndex: 0 });
    }

    onHover() {
        this.updateZIndex(100);
    }

    onBlur() {
        const { showOptions } = this.state;
        if (!showOptions) {
            this.updateZIndex();
        }
    }

    setActiveOption(option) {
        const { update } = this.props;
        this.setState({ activeOption: option });
        this.hideOptions();
        update(option);
    }

    getSelectedOptionIcon() {
        const { alt, Icon, t, shouldIconUpdate } = this.props;
        const { activeOption } = this.state;

        if (!shouldIconUpdate || !activeOption) {
            return typeof Icon === 'string' ? <img src={Icon} alt={alt} /> : <Icon />;
        }

        if (activeOption.useIcon) {
            return <img src={activeOption.icon} alt={t(activeOption.translationKey, activeOption.defaultText)} />;
        }
        return typeof Icon === 'string' ? <img src={Icon} alt={alt} /> : <Icon color={activeOption.value} />;
    }

    getCurrentSelectionText() {
        const { activeOption } = this.state;
        const { ariaLabel, options } = this.props;

        if (!activeOption) return '';

        if (Array.isArray(ariaLabel)) {
            const activeOptionIndex = options.findIndex(option => option.value === activeOption.value);

            if (activeOptionIndex === -1) {
                return '';
            }

            const currentSelectionLabel = ariaLabel[activeOptionIndex + 1];

            if (!currentSelectionLabel) {
                return '';
            }

            const mainLabel = ariaLabel[0];

            let toolPrefix = '';
            if (mainLabel.includes('Text tool')) {
                toolPrefix = 'Text size ';
            } else if (mainLabel.includes('Fill color')) {
                toolPrefix = 'Fill color ';
            } else if (mainLabel.includes('Line color')) {
                toolPrefix = 'Line color ';
            } else if (mainLabel.includes('Line thickness')) {
                toolPrefix = 'Line thickness ';
            }

            return toolPrefix ? currentSelectionLabel.replace(toolPrefix, '') : currentSelectionLabel;
        }

        return '';
    }

    hideOptions() {
        this.setState({ showOptions: false, zIndex: 0 });
    }

    toggleOptions() {
        const { showOptions } = this.state;
        if (showOptions) {
            this.hideOptions();
        } else {
            this.setState({ showOptions: true, zIndex: 100 });
        }
    }

    updateZIndex(index = 0) {
        this.setState(state => ({ ...state, zIndex: index }));
    }

    render() {
        const { isActive, options, t, tooltipTitle, tooltipText, ariaLabel, className } = this.props;
        const { showOptions, zIndex, activeOption } = this.state;

        const mainAriaLabel = Array.isArray(ariaLabel) ? ariaLabel[0] : ariaLabel;
        const enhancedAriaLabel = `${mainAriaLabel}, currently selected: ${this.getCurrentSelectionText()}`;

        return (
            <OptionsContainer style={{ zIndex }} active={isActive}>
                <StyledTooltip text={tooltipText} title={tooltipTitle} placement="right">
                    <OptionButton
                        withOptions
                        aria-label={enhancedAriaLabel}
                        aria-expanded={showOptions}
                        active={isActive}
                        onBlur={() => this.onBlur()}
                        onBlurCapture={() => this.onBlur()}
                        onClick={this.toggleOptions}
                        onFocus={() => this.onHover()}
                        onFocusCapture={() => this.onHover()}
                        onMouseOut={() => this.onBlur()}
                        onMouseOver={() => this.onHover()}
                        type="button"
                    >
                        {this.getSelectedOptionIcon()}
                    </OptionButton>
                </StyledTooltip>
                {showOptions && (
                    <OptionsList className={className}>
                        {options.map((option, index) => {
                            const optionAriaLabel = Array.isArray(ariaLabel)
                                ? ariaLabel[index + 1] || ariaLabel[0]
                                : ariaLabel;

                            return (
                                <OptionItem key={option.value}>
                                    <OptionButton
                                        type="button"
                                        className="option-item-button"
                                        aria-label={optionAriaLabel}
                                        active={activeOption && activeOption.value === option.value}
                                        onClick={() => this.setActiveOption(option)}
                                        style={
                                            option.useIcon
                                                ? { backgroundColor: '#fff' }
                                                : { backgroundColor: option.value }
                                        }
                                    >
                                        {option.useIcon ? (
                                            <OptionIcon>
                                                {option.scale ? (
                                                    <img
                                                        src={option.icon}
                                                        alt={t(option.translationKey, option.defaultText)}
                                                        style={{ transform: `scale(${option.scale})` }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={option.icon}
                                                        alt={t(option.translationKey, option.defaultText)}
                                                    />
                                                )}
                                            </OptionIcon>
                                        ) : (
                                            <OptionText>{option.value}</OptionText>
                                        )}
                                    </OptionButton>
                                </OptionItem>
                            );
                        })}
                    </OptionsList>
                )}
            </OptionsContainer>
        );
    }
}

DrawingToolOptions.defaultProps = {
    isActive: false,
    shouldIconUpdate: true,
    ariaLabel: '',
    className: '',
};

const option = PropTypes.shape({
    defaultText: PropTypes.string.isRequired,
    translationKey: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    scale: PropTypes.number,
    useIcon: PropTypes.bool.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
});
DrawingToolOptions.propTypes = {
    alt: PropTypes.string.isRequired,
    ariaLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    className: PropTypes.string,
    Icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    isActive: PropTypes.bool,
    options: PropTypes.arrayOf(option.isRequired).isRequired,
    shouldIconUpdate: PropTypes.bool,
    update: PropTypes.func.isRequired,
    value: option.isRequired,
    t: PropTypes.func.isRequired,
    tooltipTitle: PropTypes.string.isRequired,
    tooltipText: PropTypes.string.isRequired,
};

export default enhanceWithClickOutside(DrawingToolOptions);
